import React, { useState, useEffect } from 'react';
import { Intent } from '@blueprintjs/core';
import { NotificationToaster, AlertUnsavedData, AlertConfirm } from "components/notifications";
import { Button, ButtonSave } from "components/buttons";
import { FormRadio } from "components/form-fields";
import { EmployeeService } from "services";
import { UserHasAccess } from 'components/user-access';
import axios from 'axios';
import _ from 'underscore';
import { FormDragAndDropFileUploader } from 'components/form-fields/FormDragAndDropFileUploader';
import { ShowHide } from 'components/layout';
import PropTypes from 'prop-types';

export function DocumentUploader({
    onFinish,
    hideGenericOption = false,
    onCancel,
    inModal = false
}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [newDocuments, setNewDocuments] = useState([]);
    const maxFileSize = 10485760;
    const pdfFileType = 'application/pdf'
    const pdfFileExtension = '.PDF'
    const internalPermission = UserHasAccess("Internal");
    const [uploadingGeneric, setUploadingGeneric] = useState(false);

    const [showSwitchGenericConfirmation, setShowSwitchGenericConfirmation] = useState(false);

    useEffect(() => {
        if (loading) {
            axios.all([EmployeeService.getPolicyDocuments()
            ]).then(axios.spread(function (documentData) {
                setDocuments(documentData);
                setLoading(false);
            }),
            () => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, "Could not load settings. Please refresh the page.");
            }
            );
        }
    }, [loading]);

    function onIsGenericChange(event, confirmed) {
        const hasEnabledGeneric = event && event.currentTarget && event.currentTarget.value === "true";
        switchOnGenericChange(hasEnabledGeneric, confirmed);
    }

    function switchOnGenericChange(hasEnabledGeneric, confirmed) {
        if (internalPermission) {
            if (hasEnabledGeneric !== uploadingGeneric && !confirmed && newDocuments.length) {
                setShowSwitchGenericConfirmation(true);
                return;
            }

            setUploadingGeneric(hasEnabledGeneric);
        } else {
            setUploadingGeneric(false);
        }
    }

    function checkForNameClash(name, isGeneric) {
        const fileHasMatchingGenericFile = _.some(documents, function (d) {
            return d.name.toUpperCase() + pdfFileExtension === name.toUpperCase() && d.isGenericDocument && !isGeneric;
        });

        if (fileHasMatchingGenericFile) {
            return "The file name matches the name of a Logistics UK generic document. This is not allowed. Please rename the file.";
        }

        const fileHasMatchingNonGenericFile = _.some(documents, function (d) {
            return d.name.toUpperCase() + pdfFileExtension === name.toUpperCase() && !d.isGenericDocument && isGeneric;
        });

        if (fileHasMatchingNonGenericFile) {
            return "The file name matches the name of a non generic document. This is not allowed. Please rename the file.";
        }

        return "";
    }

    function validateFile(file) {
        return checkForNameClash(file.name, uploadingGeneric);
    }

    function getLatestVersion(newDocument) {
        const matchingDocument = _.find(documents, function (document) {
            return (document.name.toUpperCase() + pdfFileExtension === newDocument.name.toUpperCase()) && document.isLatestVersion;
        });

        if (matchingDocument) {
            return {
                version: matchingDocument.versionNumber + 1,
                latestVersionBeforeUpload: matchingDocument.id
            };
        } else {
            return {
                version: 1,
                latestVersionBeforeUpload: null
            };
        }
    }

    function saveDocuments() {
        setSaving(true);

        return newDocuments.map((d) => {
            const uploadRequest = {
                file: d.file,
                isGenericDocument: uploadingGeneric
            };

            if (d.version.latestVersionBeforeUpload) {
                return EmployeeService.uploadPolicyDocument(uploadRequest, d.version.latestVersionBeforeUpload)
                    .then(() => true, () => false);
            } else {
                return EmployeeService.createPolicyDocument(uploadRequest).then(() => true, () => false);
            }
        });
    }

    function onSave() {
        axios.all(saveDocuments()).then((results) => {
            const anyFailed = results.some(r => !r);
            const allFailed = results.every(r => !r);

            if (allFailed) {
                NotificationToaster.show(Intent.DANGER, "All documents failed to upload.");
                return;
            }
            else if (anyFailed) {
                NotificationToaster.show(Intent.WARNING, "Some documents failed to upload.");
            } else {
                NotificationToaster.show(Intent.SUCCESS, "All documents uploaded successfully.");
            }

            reset();
            onFinish?.();
        })
    }

    function reset() {
        setUploadingGeneric(false);
        setSaving(false);
        setNewDocuments([]);
    }

    function onConfirmingSwitchGeneric() {
        setNewDocuments([]);
        switchOnGenericChange(!uploadingGeneric, true);
        setShowSwitchGenericConfirmation(false);
    }

    function onFilesSelected(files) {
        const filesToUpload = [];

        for (let file of files) {
            const validationError = validateFile(file);

            if (validationError === "") {
                filesToUpload.push(file);
            } else {
                NotificationToaster.show(Intent.WARNING, `The file "${file.name}" cannot be uploaded. ${validationError}`);
            }
        }

        if (filesToUpload.length > 0) {
            setNewDocuments([...newDocuments, ...filesToUpload.map(f => ({
                file: f,
                version: getLatestVersion(f)
            }))]);
        }
    }

    function removeDocumentFromUploadList(fileName) {
        setNewDocuments((prevFiles) => prevFiles.filter(f => f.file.name !== fileName));
    }

    return <>
        <ShowHide evaluator={!hideGenericOption && internalPermission}
            show={(
                <FormRadio
                    disabled={saving}
                    onChange={onIsGenericChange}
                    headingText="Generic Document:"
                    selectedValue={uploadingGeneric}
                    loading={loading}
                    helperText="A generic document will be for all accounts. Uploading a non generic document will be for just this account."
                ></FormRadio>
            )}
        />
        <FormDragAndDropFileUploader
            onChange={(files) => onFilesSelected(files)}
            disabled={loading || saving}
            loading={loading}
            smaller={!!newDocuments.length}
            headingText={""}
            documents={newDocuments.map(d => ({
                name: d.file.name,
                size: d.file.size,
                loadingVersion: false,
                version: d.version.version
            }))}
            onRemoveDocument={removeDocumentFromUploadList}
            maxFileSize={maxFileSize}
            allowedFileTypes={[pdfFileType]}
            showNewVersionsAsWarning={true}
            versionWarningText={"One or more documents share their name with existing documents. Uploading these will create a new versions of the documents."}
        />

        <AlertUnsavedData
            isDirty={newDocuments.length > 0 && !saving}>
        </AlertUnsavedData>

        <div className={inModal ? "bp6-dialog-footer-actions" : "button-row"}>
            <ButtonSave
                id="btn-document-upload"
                onClick={onSave}
                disabled={saving}
                loading={loading}
            ></ButtonSave>
            <ShowHide evaluator={!!onCancel}
                show={(
                    <Button
                        onClick={onCancel}
                        text={'Cancel'}
                    />
                )}
            />
        </div>

        <AlertConfirm
            isOpen={showSwitchGenericConfirmation}
            title="Documents will be removed"
            onConfirm={() => onConfirmingSwitchGeneric()}
            onCancel={() => setShowSwitchGenericConfirmation(false)}
        >
            <p>Switching whether to upload generic documents will remove all selected files. Are you sure you want to continue?</p>
        </AlertConfirm>
    </>
}

DocumentUploader.propTypes = {
    onFinish: PropTypes.func.isRequired,
    hideGenericOption: PropTypes.bool,
    onCancel: PropTypes.func,
    inModal: PropTypes.bool
};